import { getLocalTimeZone } from "@tournated-com/ui";
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  useDashboardPlatformsControllerAccessibleCount,
  useDashboardPlatformsControllerAvailableByPublicId,
  useDashboardPlatformsControllerAvailableCount,
  useDashboardPlatformsControllerMeAvailable,
} from "@/api/__generated__/dashboard-platforms/dashboard-platforms";
import { useBrandColorContext } from "@/contexts/BrandColorContext";
import { useDateFormatContext } from "@/contexts/DateFormatContext";
import { useLocalStorage } from "@/hooks";
// Deep import avoids a circular dependency: `@/hooks` barrel transitively re-exports
// `useScopedHref`, which depends on this provider.
import { useScopedRouteParams } from "@/hooks/common/useScopedRouteParams";
import { useLanguage } from "@/i18n";
import { mapToAvailablePlatform } from "@/mappers/availablePlatform.mappers";
import { AvailablePlatform } from "@/types/availablePlatform.types";
import { PlatformId } from "@/types/platform.types";
import { ForbiddenPage } from "@/ui";
import { buildScopedPath } from "@/utils/common";
import { isPlatformForbiddenError } from "@/utils/platform";

import { useAuthContext } from "../AuthContext";

import { CurrentPlatformContext, CurrentPlatformContextValueType } from "./CurrentPlatformContext";

export const CurrentPlatformProvider = ({ children }: PropsWithChildren) => {
  const { brandColor, setBrandColor } = useBrandColorContext();
  const { setTimeZone } = useDateFormatContext();

  const [platformId, setPlatformId] = useLocalStorage<string | null>("platformId", null, {
    subscribe: "currentDocument",
  });

  const { isAuthenticated } = useAuthContext();
  const [language, changeLanguage] = useLanguage();
  const navigate = useNavigate();
  const scopedParams = useScopedRouteParams();
  const urlPlatformId = scopedParams.platformId;

  // No `capabilities` param → the legacy membership count (drives cold-start /
  // whole-platform affordances). Per-page filter visibility uses the
  // capability-scoped `usePlatformFilterCount` instead. See docs/filters.md.
  const { data: accessiblePlatformsCountData } = useDashboardPlatformsControllerAccessibleCount(
    undefined,
    { query: { enabled: isAuthenticated } }
  );
  const accessiblePlatformsCount = accessiblePlatformsCountData?.total ?? 0;

  const { data: availablePlatformsCountData } = useDashboardPlatformsControllerAvailableCount({
    query: { enabled: isAuthenticated },
  });
  const availablePlatformsCount = availablePlatformsCountData?.total ?? 0;

  const effectiveStoredId = urlPlatformId ?? platformId;

  // Stored / URL id → full record via REST `GET /platforms/available/:id`.
  // 404 means the id is outside the caller's available set (stale id —
  // role change, platform unassignment, deep-link from another tenant) OR
  // doesn't exist — both surface the same status so the body can't be used
  // as a discovery oracle. Either way the FE treats it as "clear the stored
  // id and fall back to me/available."
  const {
    data: byIdData,
    isLoading: byIdLoading,
    error: byIdError,
    refetch: refetchById,
  } = useDashboardPlatformsControllerAvailableByPublicId(effectiveStoredId ?? "", {
    query: { enabled: isAuthenticated && effectiveStoredId !== null },
  });

  const isStalePlatformId = isPlatformForbiddenError(byIdError);
  const isFatalByIdError = !!byIdError && !isStalePlatformId;

  const needsFirstFallback = isAuthenticated && (effectiveStoredId === null || isStalePlatformId);

  // First fallback (cold start / stale id) → REST `me/available`. 200 returns the
  // rich slim platform; 204 returns `void` (caller has nothing in the audience).
  const {
    data: firstDefaultData,
    isLoading: firstDefaultLoading,
    error: firstDefaultError,
    refetch: refetchFirstDefault,
  } = useDashboardPlatformsControllerMeAvailable({
    query: { enabled: needsFirstFallback },
  });

  const firstFallbackId = firstDefaultData?.id ?? null;

  const platform = useMemo<AvailablePlatform | null>(() => {
    if (byIdData && !isStalePlatformId) {
      return mapToAvailablePlatform(byIdData);
    }

    if (firstDefaultData) {
      return mapToAvailablePlatform(firstDefaultData);
    }

    return null;
  }, [byIdData, isStalePlatformId, firstDefaultData]);

  const setPlatform = useCallback(
    (platform: AvailablePlatform) => {
      setPlatformId(platform.id);
      if (platform.languages.length > 0 && !platform.languages.includes(language)) {
        changeLanguage(platform.languages[0]);
      }

      setBrandColor(platform.primaryColor);
      setTimeZone(platform.timeZone ?? getLocalTimeZone());
    },
    [setPlatformId, language, setBrandColor, changeLanguage, setTimeZone]
  );

  // Set by `switchPlatform` so the organization context can tell an intentional
  // (sidebar-style) platform switch apart from a direct URL navigation. Only the
  // former should reset the current org to the new platform's data-owner.
  const platformSwitchIntentRef = useRef(false);

  const consumePlatformSwitchIntent = useCallback(() => {
    const wasIntentional = platformSwitchIntentRef.current;
    platformSwitchIntentRef.current = false;
    return wasIntentional;
  }, []);

  const switchPlatform = useCallback(
    (nextPlatformId: PlatformId) => {
      platformSwitchIntentRef.current = true;
      setPlatformId(nextPlatformId);
      // Navigate even when there is no organization in the URL (platform-only
      // mode): `buildScopedPath` emits a platform-only path when org is null.
      // The org context's platform-switch effect then either keeps platform-only
      // (manager) or resolves the new platform's default org. The league segment
      // is always dropped — it belonged to the previous org.
      if (scopedParams.isScoped && nextPlatformId !== null) {
        // Switching platform leaves any league (a league belongs to a single org
        // under one platform) — land on the leagues list rather than carrying over
        // the now-meaningless league sub-path.
        const restPath = scopedParams.leagueId !== null ? "tours" : scopedParams.restPath;
        navigate(buildScopedPath(nextPlatformId, scopedParams.organizationId, null, restPath), {
          replace: false,
        });
      }
    },
    [
      setPlatformId,
      scopedParams.isScoped,
      scopedParams.organizationId,
      scopedParams.leagueId,
      scopedParams.restPath,
      navigate,
    ]
  );

  // Guard against re-clear loops if a fallback id also Forbiddens.
  const lastClearedStaleIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (
      platformId !== null &&
      platform !== null &&
      platform.languages.length > 0 &&
      !platform.languages.includes(language)
    ) {
      changeLanguage(platform.languages[0]);
    }

    if (platformId !== null && platform !== null && platform.primaryColor !== brandColor) {
      setBrandColor(platform.primaryColor);
    }

    if (platformId !== null && platform !== null) {
      setTimeZone(platform.timeZone ?? getLocalTimeZone());
    }

    if (isStalePlatformId && scopedParams.isScoped) {
      // URL-driven forbidden — don't silently swap; surface as ForbiddenPage below.
      return;
    }

    if (isStalePlatformId && lastClearedStaleIdRef.current !== platformId) {
      lastClearedStaleIdRef.current = platformId;
      setPlatformId(firstFallbackId);
      return;
    }

    if (platformId === null && platform !== null) {
      setPlatform(platform);
    }
  }, [
    platform,
    platformId,
    isStalePlatformId,
    firstFallbackId,
    language,
    brandColor,
    scopedParams.isScoped,
    setPlatformId,
    setPlatform,
    changeLanguage,
    setBrandColor,
    setTimeZone,
  ]);

  const refetch = useCallback(async () => {
    await Promise.all([
      effectiveStoredId !== null ? refetchById() : Promise.resolve(),
      needsFirstFallback ? refetchFirstDefault() : Promise.resolve(),
    ]);
  }, [effectiveStoredId, needsFirstFallback, refetchById, refetchFirstDefault]);

  const loading =
    (effectiveStoredId !== null && byIdLoading && !byIdData) ||
    (needsFirstFallback && firstDefaultLoading);
  const isError = isFatalByIdError || !!firstDefaultError;

  const contextValue = useMemo(
    (): CurrentPlatformContextValueType => ({
      platform,
      accessiblePlatformsCount,
      availablePlatformsCount,
      isInitialized: !loading && !!platform,
      switchPlatform,
      consumePlatformSwitchIntent,
      loading,
      refetch,
      isError,
    }),
    [
      platform,
      accessiblePlatformsCount,
      availablePlatformsCount,
      switchPlatform,
      consumePlatformSwitchIntent,
      loading,
      refetch,
      isError,
    ]
  );

  if (scopedParams.isScoped && isStalePlatformId) {
    return <ForbiddenPage />;
  }

  return (
    <CurrentPlatformContext.Provider value={contextValue}>
      {children}
    </CurrentPlatformContext.Provider>
  );
};
